import { useState } from "react";
import type { ReplyChannel, ReplyWindow } from "@school-sales-ai/shared";
import { Button } from "@/components/ui/button";
import { useSendReply } from "@/hooks/useSendReply";
import {
  describeReplyWindow,
  formatRemaining,
  type ReplyWindowState,
} from "@/lib/reply-window";
import { cn } from "@/lib/utils";

type Props = {
  leadId: string;
  /** The WhatsApp window. Ignored when `whatsappAvailable` is false. */
  replyWindow: ReplyWindow | null | undefined;
  /** Null for a lead with no WhatsApp number — nothing to send to. */
  phone: string | null;
  aiPaused: boolean;
  /** False for a lead that only ever wrote on Instagram. Default true. */
  whatsappAvailable?: boolean;
  /** The Instagram chat, for a lead that writes there. */
  instagram?: { window: ReplyWindow | null | undefined } | null;
  /** Where the box opens: the channel of the prospect's last message. */
  defaultChannel?: ReplyChannel | null;
};

const CHANNEL_LABEL: Record<ReplyChannel, string> = {
  whatsapp: "WhatsApp",
  instagram: "Instagram",
};

// Mirrors the `status → classes` table in components/visits/visit-status.ts.
// Amber is this app's "needs attention"; ink is inert.
const WINDOW_CHIP: Record<ReplyWindowState, string> = {
  none: "",
  never: "bg-ink-100 text-ink-500",
  open: "bg-ink-050 text-ink-500",
  closing: "bg-amber-50 text-amber-700",
  closed: "bg-amber-50 text-amber-700",
};

/**
 * Reply box for the lead's conversation, on WhatsApp or Instagram.
 *
 * The 24-hour window is the point of this component, not a detail of it: the
 * team cannot see it anywhere else, and discovering it as a failed send is the
 * worst possible way to learn about it. So the remaining time is always on
 * screen while a window exists, and when it has closed the box refuses in place
 * and says what to do instead.
 *
 * A lead who writes on both (a merged lead) gets a WhatsApp | Instagram switch,
 * opened on the chat they last wrote in; each channel keeps its own window.
 *
 * Follows NoteInput's idiom (owns its mutation, ⌘/Ctrl+Enter, keeps the text on
 * failure, inline error, label-swapping button) rather than the widget's
 * MessageInput, which has no room to explain itself.
 */
export function ReplyComposer({
  leadId,
  replyWindow,
  phone,
  aiPaused,
  whatsappAvailable = true,
  instagram = null,
  defaultChannel = null,
}: Props): React.JSX.Element {
  const channels: ReplyChannel[] = [
    ...(whatsappAvailable ? (["whatsapp"] as const) : []),
    ...(instagram ? (["instagram"] as const) : []),
  ];
  const [selected, setSelected] = useState<ReplyChannel | null>(null);
  const channel: ReplyChannel =
    selected ??
    (defaultChannel && channels.includes(defaultChannel) ? defaultChannel : (channels[0] ?? "whatsapp"));
  const [content, setContent] = useState("");
  const { mutate, isPending, error } = useSendReply(leadId);

  const view = describeReplyWindow(channel === "instagram" ? instagram?.window : replyWindow);
  const noPhone = channel === "whatsapp" && !phone;
  const blocked = noPhone || !view.canSend;

  function submit(): void {
    const trimmed = content.trim();
    if (!trimmed || isPending || blocked) return;
    // Cleared only on success, so a failed send never loses what was typed.
    mutate({ content: trimmed, channel }, { onSuccess: () => setContent("") });
  }

  return (
    <div className="flex flex-col gap-2">
      {channels.length > 1 && (
        <div role="radiogroup" aria-label="Responder por" className="flex gap-1 self-start rounded-lg bg-ink-050 p-0.5">
          {channels.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={c === channel}
              onClick={() => setSelected(c)}
              className={cn(
                "rounded-md px-2.5 py-1 text-xs font-medium",
                c === channel ? "bg-surface text-ink-900 shadow-sm" : "text-ink-500",
              )}
            >
              {CHANNEL_LABEL[c]}
            </button>
          ))}
        </div>
      )}
      {noPhone ? (
        <p className="text-xs text-ink-500">
          Este prospecto no tiene número de WhatsApp registrado.
        </p>
      ) : (
        <WindowNotice channel={channel} state={view.state} remainingMs={view.remainingMs} />
      )}

      <textarea
        rows={2}
        value={content}
        disabled={isPending || blocked}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        placeholder={
          blocked
            ? "No se puede escribir a este prospecto ahora"
            : "Escribe tu respuesta… (⌘↵ para enviar)"
        }
        className="w-full resize-none rounded-lg border border-ink-300 bg-surface px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus-visible:border-lime-dk focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-lime-dk disabled:opacity-50"
      />

      {error && <p className="text-xs text-red-500">{(error as Error).message}</p>}

      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-ink-400">
          {aiPaused
            ? "La IA está pausada en este chat."
            : "Al responder, la IA se pausa en este chat."}
        </span>
        <Button
          size="sm"
          onClick={submit}
          disabled={isPending || blocked || !content.trim()}
        >
          {isPending ? "Enviando…" : "Enviar"}
        </Button>
      </div>
    </div>
  );
}

// What each channel's closed window means, in the words of its own rule.
const CLOSED_COPY: Record<ReplyChannel, { closed: string; closedBody: string; never: string; neverBody: string }> = {
  whatsapp: {
    closed: "La ventana de 24 h se cerró",
    closedBody:
      "WhatsApp solo permite escribir dentro de las 24 h posteriores al último mensaje del prospecto. Espera a que vuelva a escribir, o llámale por teléfono.",
    never: "Este prospecto nunca ha escrito por WhatsApp",
    neverBody: "WhatsApp solo permite iniciar la conversación cuando el prospecto escribe primero.",
  },
  instagram: {
    closed: "Pasaron 7 días desde su último mensaje",
    closedBody:
      "Instagram solo permite responder hasta 7 días después del último mensaje del prospecto. Espera a que vuelva a escribir.",
    never: "Este prospecto nunca ha escrito por Instagram",
    neverBody: "Instagram solo permite escribir cuando el prospecto escribe primero.",
  },
};

function WindowNotice({
  channel,
  state,
  remainingMs,
}: {
  channel: ReplyChannel;
  state: ReplyWindowState;
  remainingMs: number | null;
}): React.JSX.Element | null {
  // Baileys tenants have no window at all; showing them a countdown would be a
  // lie, so this renders nothing for them.
  if (state === "none") return null;

  if (state === "closed" || state === "never") {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
        <p className="font-semibold">
          {state === "closed" ? CLOSED_COPY[channel].closed : CLOSED_COPY[channel].never}
        </p>
        <p className="mt-0.5">
          {state === "closed" ? CLOSED_COPY[channel].closedBody : CLOSED_COPY[channel].neverBody}
        </p>
      </div>
    );
  }

  return (
    <span
      className={cn(
        "self-start rounded-full px-2 py-0.5 text-[10px] font-medium",
        WINDOW_CHIP[state],
      )}
    >
      {state === "closing" ? "Quedan " : "Puedes responder por "}
      {remainingMs !== null ? formatRemaining(remainingMs) : ""}
    </span>
  );
}
